import React, { useState, useEffect, useRef } from 'react';
import styles from './AuditHistory.module.scss';
import loadingGif from '../../../../Assets/Animations/loading.gif';
import { userApi } from '../../../../Utils/API';
import useLogout from '../../../../hooks/useLogout';

const ITEMS_PER_LOAD = 6;

const AuditHistory = () => {
    const [historyData, setHistoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [visibleItems, setVisibleItems] = useState(ITEMS_PER_LOAD);
    const [scrollLoading, setScrollLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'generatedOn', direction: 'descending' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const tableContainerRef = useRef(null);
    const loadingRef = useRef(false);
    const logout = useLogout();

    useEffect(() => {
        const fetchAuditHistory = async () => {
            try {
                const token_type = localStorage.getItem('token_type');
                const access_token = localStorage.getItem('access_token');

                // if (!token_type || !access_token) {
                //     throw new Error('Authorization token not found.');
                // }

                const response = await userApi.get('/api/my-profile/expired-secret-keys', {
                    headers: {
                        Authorization: `${token_type} ${access_token}`,
                    },
                });

                const transformedData = response.data.map(item => ({
                    ...item,
                    secretKey: item.maskedSecret,
                }));

                setHistoryData(transformedData);
            } catch (err) {
                if (err.response?.status === 401) {
                    logout();
                } else {
                    console.error("Failed to fetch audit history.", err);
                    setError(err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAuditHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let data = [...historyData];

        if (searchTerm) {
            data = data.filter(item =>
                item.secretKey.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (sortConfig.key) {
            data.sort((a, b) => {
                const aValue = new Date(a[sortConfig.key]);
                const bValue = new Date(b[sortConfig.key]);

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredData(data);
        setVisibleItems(ITEMS_PER_LOAD);
    }, [historyData, searchTerm, sortConfig]);

    const handleScroll = () => {
        if (loadingRef.current || visibleItems >= filteredData.length) return;

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;

        if (scrollHeight - scrollTop <= clientHeight + 50) {
            loadingRef.current = true;
            setScrollLoading(true);
            setTimeout(() => {
                setVisibleItems((prev) => prev + ITEMS_PER_LOAD);
                setScrollLoading(false);
                loadingRef.current = false;
            }, 1000);
        }
    };

    useEffect(() => {
        const tableContainer = tableContainerRef.current;
        if (tableContainer) {
            tableContainer.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (tableContainer) {
                tableContainer.removeEventListener('scroll', handleScroll);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleItems, filteredData]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return null;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        };
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <img src={loadingGif} alt="Loading..." />
            </div>
        );
    }

    if (error) {
        return <div className={styles.errorContainer}>Failed to load audit history.</div>;
    }

    return (
        <div className={styles.auditContainer}>
            <div className={styles.auditHeader}>
                <h3 className={styles.title}>API Secret Key</h3>
                <div className={styles.tableActions}>
                    <input
                        type="text"
                        placeholder="Search by Secret Key..."
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <div className={styles.tableContainer} ref={tableContainerRef}>
                <table className={styles.auditTable}>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('secretKey')}>Secret Key {getSortIndicator('secretKey')}</th>
                            <th onClick={() => requestSort('generatedOn')}>Generated On {getSortIndicator('generatedOn')}</th>
                            <th onClick={() => requestSort('expiresOn')}>Expires On {getSortIndicator('expiresOn')}</th>
                            <th className={styles.statusHeader}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.slice(0, visibleItems).map((record, index) => (
                                <tr key={index}>
                                    <td>{record.secretKey}</td>
                                    <td>{formatDate(record.generatedOn)}</td>
                                    <td>{formatDate(record.expiresOn)}</td>
                                    <td>
                                        <div className={styles.statusExpired}>
                                            <i className="material-icons">hourglass_disabled</i>
                                            <span>Expired</span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className={styles.noDataCell}>
                                    No expired secret keys found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {scrollLoading && (
                    <div className={styles.loadingContainer}>
                        <img src={loadingGif} alt="Loading..." />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuditHistory;